import { memo, useEffect, useState } from "react"
import { Badge, Box, Button, Card, HStack, Input, Stack, Text } from "@chakra-ui/react"
import WithLoginPage from "@/components/withlogin"
import DebtService from "services/debt"
import { formatCurrency } from "@/utilities/number"
import moment from "moment"
import { useApp } from "contexts/app"
import BottomSheet from "components/bottom-sheet"

const service = new DebtService();

const quickRanges = [
    {
        key: "prev_week",
        label: "Tuần trước",
        getDate: () => moment().subtract(1, "week").startOf("week").format("YYYY-MM-DD"),
    },
    {
        key: "prev_month",
        label: "Tháng trước",
        getDate: () => moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
    },
    {
        key: "prev_2_month",
        label: "2 tháng trước",
        getDate: () => moment().subtract(2, "month").startOf("month").format("YYYY-MM-DD"),
    },
    {
        key: "prev_3_month",
        label: "3 tháng trước",
        getDate: () => moment().subtract(3, "month").startOf("month").format("YYYY-MM-DD"),
    },
];

const DebtPage = () => {
    const { setPageTitle, setProcessing } = useApp();
    const [items, setItems] = useState([]);
    const [fromDate, setFromDate] = useState(moment().subtract(1, "month").format("YYYY-MM-DD"));
    const [openingBalance, setOpeningBalance] = useState(0);
    const [openFilterSheet, setOpenFilterSheet] = useState(false);

    useEffect(() => {
        setPageTitle("Công nợ");
    }, [setPageTitle]);

    useEffect(() => {
        loadData();
    }, [fromDate]);

    const loadData = async () => {
        setProcessing(true);
        try {
            const listData = await service.list({ from_date: fromDate });
            setItems(Array.isArray(listData?.items) ? listData.items : []);
            setOpeningBalance(Number(listData?.opening_balance || 0));
        } finally {
            setProcessing(false);
        }
    };

    const periodDebt = items
        .filter((item) => item.type === "debt")
        .reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const periodPayment = items
        .filter((item) => item.type === "payment")
        .reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const remainingDebt = openingBalance + periodDebt - periodPayment;

    const onQuickSelect = (getDate) => {
        setFromDate(getDate());
        setOpenFilterSheet(false);
    };

    return (
        <div style={{ paddingBottom: 75 }}>
            <Stack gap={3}>
                <Card.Root size="sm">
                    <Card.Header>
                        <Text fontWeight="bold">Tổng quan công nợ</Text>
                    </Card.Header>
                    <Card.Body>
                        <Stack gap={3}>
                            <HStack justify="space-between" align="center">
                                <Text color="fg.muted">Từ ngày</Text>
                                <Button size="sm" variant="ghost" onClick={() => setOpenFilterSheet(true)}>
                                    {moment(fromDate).format("DD/MM/YYYY")}
                                </Button>
                            </HStack>

                            <HStack justify="space-between">
                                <Text color="fg.muted">Nợ cũ</Text>
                                <Text>{formatCurrency(openingBalance)}</Text>
                            </HStack>
                            <HStack justify="space-between">
                                <Text color="fg.muted">Mua hàng</Text>
                                <Text color="orange.600">+{formatCurrency(periodDebt)}</Text>
                            </HStack>
                            <HStack justify="space-between">
                                <Text color="fg.muted">Đã thanh toán</Text>
                                <Text color="green.600">-{formatCurrency(periodPayment)}</Text>
                            </HStack>
                            <HStack justify="space-between">
                                <Text color="fg.muted">Còn nợ</Text>
                                <Text fontWeight="bold">{formatCurrency(remainingDebt)}</Text>
                            </HStack>
                        </Stack>
                    </Card.Body>
                </Card.Root>

                <Card.Root size="sm">
                    <Card.Header>
                        <Text fontWeight="bold">Lịch sử công nợ</Text>
                    </Card.Header>
                    <Card.Body>
                        {items.length === 0 && (
                            <Text color="fg.muted">Chưa có phát sinh công nợ.</Text>
                        )}

                        {items.length > 0 && (
                            <Stack gap={2}>
                                {items.map((item) => {
                                    const isPayment = item.type === "payment";
                                    return (
                                        <Box
                                            key={item.id}
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            borderRadius="md"
                                            p={3}
                                        >
                                            <HStack justify="space-between" align="start" mb={2}>
                                                <Stack gap={1}>
                                                    <Text fontWeight="semibold">
                                                        {moment(item.date || item.create_on).format("DD/MM/YYYY")}
                                                    </Text>
                                                    <Badge width="fit-content" colorPalette={isPayment ? "green" : "orange"}>
                                                        {isPayment ? "Thanh toán" : "Mua hàng"}
                                                    </Badge>
                                                </Stack>
                                                <Text fontWeight="bold" color={isPayment ? "green.600" : "orange.600"}>
                                                    {isPayment ? "-" : "+"}{formatCurrency(item.amount ?? 0)}
                                                </Text>
                                            </HStack>

                                            <Text>{item.description || "-"}</Text>
                                            {item.order_id ? (
                                                <Text color="fg.muted" fontSize="xs" mt={1}>
                                                    Đơn: {item.order_id}
                                                </Text>
                                            ) : null}
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Card.Body>
                </Card.Root>
            </Stack>

            <BottomSheet open={openFilterSheet} onOpenChange={(e) => setOpenFilterSheet(e)} title="Chọn mốc thời gian">
                <Stack gap={3}>
                    {quickRanges.map((range) => (
                        <Button key={range.key} variant="outline" onClick={() => onQuickSelect(range.getDate)}>
                            {range.label}
                        </Button>
                    ))}
                    <Input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />
                    <Button variant="ghost" onClick={() => setOpenFilterSheet(false)}>Đóng</Button>
                </Stack>
            </BottomSheet>
        </div>
    )
}

const withLogin = () => (<WithLoginPage><DebtPage></DebtPage></WithLoginPage>)

export default memo(withLogin)